import React, { useState } from 'react';
import { 
  CreateOrganization, 
  OrganizationSwitcher,
  useOrganization,
  useUser
} from '@clerk/clerk-react';
import { FiPlus, FiUsers, FiChevronDown } from 'react-icons/fi';

const OrganizationManagement = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { organization } = useOrganization();
  const { user } = useUser();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <FiUsers className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Organization Management</h2>
        </div>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus size={16} />
            <span>Create Organization</span>
          </button>
        )}
      </div>

      {/* Current Organization Info */}
      {organization && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-700 mb-2">Current Organization</h3>
          <div className="flex items-center space-x-3">
            {organization.imageUrl && (
              <img 
                src={organization.imageUrl} 
                alt={organization.name}
                className="w-10 h-10 rounded-full"
              />
            )}
            <div>
              <p className="font-semibold text-gray-800">{organization.name}</p>
              <p className="text-sm text-gray-500">
                {organization.membersCount} member{organization.membersCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Organization Switcher */}
      <div className="mb-6">
        <h3 className="font-medium text-gray-700 mb-3">Switch Organization</h3>
        <div className="border border-gray-200 rounded-lg">
          <OrganizationSwitcher 
            appearance={{
              elements: {
                organizationSwitcherTrigger: "w-full p-3 text-left hover:bg-gray-50 rounded-lg transition-colors",
                organizationSwitcherPopoverCard: "shadow-lg border border-gray-200",
                organizationSwitcherPopoverActions: "p-2",
                organizationSwitcherPopoverActionButton: "w-full text-left p-2 hover:bg-gray-100 rounded transition-colors"
              }
            }}
          />
        </div>
      </div>

      {/* Create Organization Form */}
      {showCreateForm && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-700">Create New Organization</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <CreateOrganization 
            appearance={{
              elements: {
                formButtonPrimary: "bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors",
                formFieldInput: "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                formFieldLabel: "block text-sm font-medium text-gray-700 mb-1"
              }
            }}
            afterCreateOrganizationUrl="/dashboard"
            // Add additional redirect configuration for invite flows
            afterInviteMemberUrl="/dashboard"
            afterSkipInvitationUrl="/dashboard"
            afterAcceptInvitationUrl="/dashboard"
            afterDeclineInvitationUrl="/dashboard"
          />
        </div>
      )}

      {/* User Info */}
      {user && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-medium text-gray-700 mb-2">Your Account</h3>
          <div className="flex items-center space-x-3">
            {user.imageUrl && (
              <img 
                src={user.imageUrl} 
                alt={user.fullName}
                className="w-8 h-8 rounded-full"
              />
            )}
            <div>
              <p className="font-medium text-gray-800">{user.fullName}</p>
              <p className="text-sm text-gray-500">{user.primaryEmailAddress?.emailAddress}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement; 